import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Alert,
  CircularProgress, Checkbox, FormControlLabel, IconButton,
  InputAdornment, Divider,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/auth.service';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';

/* ── Design tokens ── */
const T = {
  purple: '#552583',
  purpleDark: '#3a1760',
  gold: '#FDB927',
  bg: '#E4F0F6',
  card: '#ffffff',
  text: '#1a1a1a',
  muted: 'rgba(0,0,0,0.45)',
  subtle: 'rgba(0,0,0,0.08)',
  inputBg: 'rgba(85,37,131,0.03)',
  inputBorder: 'rgba(85,37,131,0.14)',
  inputFocus: 'rgba(85,37,131,0.45)',
  radius: { input: '10px', btn: '12px', card: '24px', alert: '12px' },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: T.inputBg,
    borderRadius: T.radius.input,
    transition: 'border-color 0.2s',
    '& fieldset': { borderColor: T.inputBorder },
    '&:hover fieldset': { borderColor: 'rgba(85,37,131,0.3)' },
    '&.Mui-focused fieldset': { borderColor: T.inputFocus, borderWidth: '1.5px' },
  },
  '& input': { color: T.text, py: 1.5, fontSize: '0.95rem', '&::placeholder': { color: 'rgba(0,0,0,0.28)' } },
};

const labelSx = {
  color: T.muted,
  mb: 0.8,
  display: 'block',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  fontSize: '0.68rem',
};

const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

const AppleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="currentColor" d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const AppleButton = ({ onClick, loading }) => (
  <Button
    fullWidth onClick={onClick} disabled={loading}
    sx={{
      py: 1.4, px: 2, borderRadius: T.radius.btn,
      background: '#000', border: '1px solid #000',
      color: '#fff', fontWeight: 600, fontSize: '0.9rem',
      textTransform: 'none', display: 'flex', alignItems: 'center', gap: 1.5,
      boxShadow: 'none',
      transition: 'all 0.15s ease',
      '&:hover': { background: '#1a1a1a', borderColor: '#1a1a1a' },
      '&:active': { background: '#333' },
      '&:disabled': { opacity: 0.5 },
    }}
  >
    {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <AppleLogo />}
    <span>Continuer avec Apple</span>
  </Button>
);

const GoogleButton = ({ onClick, loading, text = 'Continuer avec Google' }) => (
  <Button
    fullWidth onClick={onClick} disabled={loading}
    sx={{
      py: 1.4, px: 2, borderRadius: T.radius.btn,
      background: 'white', border: '1px solid rgba(0,0,0,0.13)',
      color: '#333', fontWeight: 600, fontSize: '0.9rem',
      textTransform: 'none', display: 'flex', alignItems: 'center', gap: 1.5,
      boxShadow: 'none',
      transition: 'all 0.15s ease',
      '&:hover': { background: '#fafafa', borderColor: 'rgba(0,0,0,0.25)', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
      '&:active': { background: '#f5f5f5' },
      '&:disabled': { opacity: 0.5 },
    }}
  >
    {loading ? <CircularProgress size={18} sx={{ color: '#4285F4' }} /> : <GoogleLogo />}
    <span>{text}</span>
  </Button>
);

const CredentialsStep = ({ onSuccess, onLeaked, onUnverified, onGoogleSuccess, onAppleSuccess, onError, onRememberMe }) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleAppleClick = async () => {
    setAppleLoading(true);
    try {
      const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || 'yt.usafiri.web';
      if (!window.AppleID) {
        onError('Apple Sign-In non chargé. Rechargez la page.');
        return;
      }
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: window.location.origin,
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      const { id_token } = response.authorization;
      const name = response.user?.name;
      await onAppleSuccess({
        identity_token: id_token,
        first_name: name?.firstName || null,
        last_name: name?.lastName || null,
      });
    } catch (err) {
      if (err?.error !== 'popup_closed_by_user') {
        onError('Connexion Apple échouée. Réessayez.');
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const handleGoogleClick = () => {
    setGoogleLoading(true);
    try {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '854957052556-5n2rr4mh45uujceh8a60v837knoni7vk.apps.googleusercontent.com';
      if (!window.google?.accounts?.id) {
        onError('Google Sign-In non chargé. Rechargez la page.');
        setGoogleLoading(false);
        return;
      }
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          try {
            await onGoogleSuccess({ credential: response.credential });
          } catch (err) {
            onError(err.message || 'Erreur connexion Google');
          }
          setGoogleLoading(false);
        },
      });
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          window.google.accounts.oauth2.initCodeClient({
            client_id: clientId,
            scope: 'email profile',
            ux_mode: 'popup',
            callback: async () => {},
          });
          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: clientId,
            scope: 'email profile',
            callback: async (tokenResponse) => {
              if (tokenResponse.access_token) {
                try {
                  await onGoogleSuccess({ access_token: tokenResponse.access_token });
                } catch (err) {
                  onError(err.message || 'Erreur connexion Google');
                }
              }
              setGoogleLoading(false);
            },
          });
          tokenClient.requestAccessToken();
        }
      });
    } catch {
      onError('Erreur lors de la connexion Google');
      setGoogleLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData((p) => ({ ...p, [e.target.name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    authService.setRememberMe(formData.rememberMe);
    onRememberMe?.(formData.rememberMe);
    try {
      const data = await login(formData.email, formData.password, null);
      onSuccess(data);
    } catch (err) {
      if (err.response?.data?.passwordLeaked) {
        onLeaked();
      } else if (err.response?.data?.requiresEmailVerification) {
        onUnverified(err.response.data.verificationToken);
      } else {
        onError(err.response?.data?.message || 'Email ou mot de passe incorrect');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
        <GoogleButton onClick={handleGoogleClick} loading={googleLoading} text="Continuer avec Google" />
        <AppleButton onClick={handleAppleClick} loading={appleLoading} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
        <Divider sx={{ flex: 1, borderColor: 'rgba(0,0,0,0.08)' }} />
        <Typography sx={{ px: 2, color: 'rgba(0,0,0,0.3)', fontSize: '0.75rem', fontWeight: 500 }}>ou</Typography>
        <Divider sx={{ flex: 1, borderColor: 'rgba(0,0,0,0.08)' }} />
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" sx={labelSx}>Adresse email</Typography>
        <TextField
          fullWidth name="email" type="email" placeholder="votre@email.com"
          value={formData.email} onChange={handleChange} required autoFocus
          InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ color: 'rgba(85,37,131,0.35)', fontSize: 19 }} /></InputAdornment> }}
          sx={inputSx}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" sx={labelSx}>Mot de passe</Typography>
          <Link to="/forgot-password" style={{ color: T.purple, textDecoration: 'none', fontSize: '0.78rem', fontWeight: 600 }}>
            Mot de passe oublié ?
          </Link>
        </Box>
        <TextField
          fullWidth name="password" type={showPassword ? 'text' : 'password'}
          placeholder="Entrez votre mot de passe"
          value={formData.password} onChange={handleChange} required
          InputProps={{
            startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: 'rgba(85,37,131,0.35)', fontSize: 19 }} /></InputAdornment>,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" sx={{ color: 'rgba(85,37,131,0.35)', '&:hover': { color: T.purple } }}>
                  {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={inputSx}
        />
      </Box>

      <Box sx={{ mb: 3.5 }}>
        <FormControlLabel
          control={<Checkbox name="rememberMe" checked={formData.rememberMe} onChange={handleChange} size="small" sx={{ color: 'rgba(85,37,131,0.25)', '&.Mui-checked': { color: T.purple } }} />}
          label={<Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.45)', fontSize: '0.84rem' }}>Se souvenir de moi</Typography>}
        />
      </Box>

      <Button
        type="submit" fullWidth variant="contained" disabled={loading}
        endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
        sx={{
          py: 1.6, mb: 3,
          background: T.purple,
          color: 'white', fontWeight: 700, fontSize: '0.95rem', borderRadius: T.radius.btn,
          textTransform: 'none', letterSpacing: '0.01em',
          boxShadow: '0 4px 16px rgba(85,37,131,0.25)',
          transition: 'all 0.15s ease',
          '&:hover': { background: T.purpleDark, boxShadow: '0 6px 24px rgba(85,37,131,0.35)' },
          '&:active': { boxShadow: '0 2px 8px rgba(85,37,131,0.2)' },
          '&:disabled': { background: 'rgba(85,37,131,0.15)', color: 'rgba(0,0,0,0.3)' },
        }}
      >
        {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Se connecter'}
      </Button>

      <Box textAlign="center">
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.4)', fontSize: '0.88rem' }}>
          Pas encore de compte ?{' '}
          <Link to="/register" style={{ color: T.purple, textDecoration: 'none', fontWeight: 700 }}>
            Créer un compte
          </Link>
        </Typography>
      </Box>
    </form>
  );
};

const OtpStep = ({ method, twoFactorSession, hints, onBack, onError, rememberMe }) => {
  const { verify2FACode, send2FACode } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    authService.setRememberMe(rememberMe ?? true);
    try {
      await verify2FACode(twoFactorSession, code, rememberDevice);
      navigate('/dashboard');
    } catch (err) {
      onError(err.response?.data?.message || 'Code incorrect ou expiré');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResent(false);
    try {
      await send2FACode(twoFactorSession, method, null);
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (err) {
      onError(err.response?.data?.message || 'Erreur lors du renvoi');
    } finally {
      setResending(false);
    }
  };

  const dest = method === 'email' ? hints.email : hints.phone;

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: '16px', mx: 'auto', mb: 2,
          background: 'rgba(85,37,131,0.08)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <EmailOutlinedIcon sx={{ fontSize: 26, color: T.purple }} />
        </Box>
        <Typography variant="h5" sx={{ color: T.text, fontWeight: 800, mb: 0.5, fontSize: '1.4rem' }}>
          Vérifiez votre email
        </Typography>
        <Typography variant="body2" sx={{ color: T.muted, lineHeight: 1.6 }}>
          Un code à 6 chiffres a été envoyé à<br />
          <Box component="span" sx={{ color: T.purple, fontWeight: 700 }}>{dest}</Box>
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" sx={labelSx}>Code de vérification</Typography>
        <TextField
          fullWidth autoFocus
          value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          sx={{
            ...inputSx,
            '& input': { color: T.text, py: 1.6, letterSpacing: '0.4em', fontSize: '1.5rem', fontWeight: 700, textAlign: 'center', '&::placeholder': { color: 'rgba(0,0,0,0.12)', letterSpacing: '0.3em', fontSize: '1.1rem' } },
          }}
        />
      </Box>

      <FormControlLabel
        control={<Checkbox checked={rememberDevice} onChange={e => setRememberDevice(e.target.checked)} size="small" sx={{ color: 'rgba(85,37,131,0.25)', '&.Mui-checked': { color: T.purple } }} />}
        label={<Typography sx={{ fontSize: '0.85rem', color: T.muted }}>Se souvenir de cet appareil pendant 30 jours</Typography>}
        sx={{ mb: 2, ml: 0 }}
      />

      <Button
        type="submit" fullWidth variant="contained" disabled={loading || code.length !== 6}
        endIcon={!loading && <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
        sx={{
          py: 1.6, mb: 3,
          background: T.purple, color: 'white', fontWeight: 700, fontSize: '0.95rem',
          borderRadius: T.radius.btn, textTransform: 'none',
          boxShadow: '0 4px 16px rgba(85,37,131,0.25)',
          '&:hover': { background: T.purpleDark },
          '&:disabled': { background: 'rgba(85,37,131,0.12)', color: 'rgba(0,0,0,0.25)' },
        }}
      >
        {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Confirmer la connexion'}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        {resent ? (
          <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}>
            <CheckIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.3 }} />Code renvoyé
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.4)', mb: 1.5, fontSize: '0.85rem' }}>
            Pas recu ?{' '}
            <Box
              component="span"
              onClick={!resending ? handleResend : undefined}
              sx={{ color: T.purple, fontWeight: 700, cursor: resending ? 'default' : 'pointer', textDecoration: 'underline', opacity: resending ? 0.5 : 1 }}
            >
              {resending ? 'Envoi...' : 'Renvoyer'}
            </Box>
          </Typography>
        )}
        <Button
          onClick={onBack}
          sx={{ color: 'rgba(0,0,0,0.35)', textTransform: 'none', fontSize: '0.82rem', '&:hover': { color: T.purple, background: 'transparent' } }}
        >
          Retour
        </Button>
      </Box>
    </form>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const { googleLogin, appleLogin, verifyEmail, resendVerificationEmail } = useAuth();

  const [step, setStep] = useState('credentials');
  const [twoFactorSession, setTwoFactorSession] = useState(null);
  const [hints, setHints] = useState({});
  const [method, setMethod] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');

  const [verificationToken, setVerificationToken] = useState('');
  const [verifCode, setVerifCode] = useState('');
  const [verifLoading, setVerifLoading] = useState(false);
  const [verifResending, setVerifResending] = useState(false);
  const [verifResent, setVerifResent] = useState(false);

  const handleLoginSuccess = (data) => {
    setError('');
    if (data.requiresTwoFactor) {
      setTwoFactorSession(data.twoFactorSession);
      setHints(data.hints);
      setMethod('email');
      setStep('otp');
    } else {
      navigate('/dashboard');
    }
  };

  const handleUnverified = (token) => {
    setError('');
    setVerificationToken(token);
    setStep('verify-email');
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setVerifLoading(true); setError('');
    try {
      await verifyEmail(verificationToken, verifCode);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Code incorrect ou expiré');
    } finally { setVerifLoading(false); }
  };

  const handleVerifResend = async () => {
    setVerifResending(true); setVerifResent(false);
    try {
      await resendVerificationEmail(verificationToken);
      setVerifResent(true);
      setTimeout(() => setVerifResent(false), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du renvoi');
    } finally { setVerifResending(false); }
  };

  const handleGoogleSuccess = async ({ credential, access_token }) => {
    setError('');
    try {
      await googleLogin(credential ? { credential } : { access_token });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la connexion Google');
    }
  };

  const handleAppleSuccess = async ({ identity_token, first_name, last_name }) => {
    setError('');
    try {
      await appleLogin({ identity_token, first_name, last_name });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la connexion Apple');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', background: T.bg }}>

      {/* ── Left branding ── */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column', justifyContent: 'space-between',
        width: '42%', minHeight: '100vh',
        background: T.purple,
        p: 6, position: 'relative', overflow: 'hidden',
      }}>
        {/* Single accent shape — intentionally asymmetric */}
        <Box sx={{
          position: 'absolute', bottom: -120, right: -80,
          width: 400, height: 400, borderRadius: '50%',
          background: 'rgba(253,185,39,0.12)',
          pointerEvents: 'none',
        }} />

        <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '0.15em', color: 'white', position: 'relative', zIndex: 1 }}>
          USAFIRI
        </Typography>

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 380 }}>
          <Typography sx={{
            fontWeight: 800, fontSize: '2.8rem', lineHeight: 1.1,
            color: 'white', mb: 2, letterSpacing: '-0.03em',
          }}>
            Bon retour
            <Box component="span" sx={{ display: 'block', color: 'rgba(255,255,255,0.35)', fontSize: '2.2rem', mt: 0.5 }}>
              parmi nous.
            </Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.92rem', lineHeight: 1.7 }}>
            Restaurants, livraisons, VTC et location de véhicules à Mayotte.
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
            usafiri.yt
          </Typography>
        </Box>
      </Box>

      {/* ── Right form ── */}
      <Box sx={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        p: { xs: 3, sm: 5, md: 6 },
      }}>
        <Box sx={{ width: '100%', maxWidth: 420 }}>

          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: '0.15em', color: T.purple }}>USAFIRI</Typography>
          </Box>

          {/* Card */}
          <Box sx={{
            background: T.card,
            borderRadius: T.radius.card,
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 12px 40px rgba(85,37,131,0.06)',
            p: { xs: 3.5, sm: 4.5 },
          }}>

            {step === 'credentials' && (
              <Box sx={{ mb: 3.5 }}>
                <Typography sx={{ color: T.text, fontWeight: 800, mb: 0.5, fontSize: { xs: '1.4rem', sm: '1.6rem' }, letterSpacing: '-0.02em' }}>
                  Connexion
                </Typography>
                <Typography sx={{ color: T.muted, fontSize: '0.88rem' }}>
                  Accédez à votre espace personnel
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2.5, background: 'rgba(231,76,60,0.06)', border: '1px solid rgba(231,76,60,0.15)', borderRadius: T.radius.alert, '& .MuiAlert-icon': { color: '#e74c3c' } }}>
                {error}
              </Alert>
            )}

            {step === 'credentials' && (
              <CredentialsStep
                onSuccess={handleLoginSuccess}
                onLeaked={() => { setError(''); setStep('leaked'); }}
                onUnverified={handleUnverified}
                onGoogleSuccess={handleGoogleSuccess}
                onAppleSuccess={handleAppleSuccess}
                onError={setError}
                onRememberMe={setRememberMe}
              />
            )}

            {step === 'leaked' && (
              <Box>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: '16px', background: 'rgba(231,76,60,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                    <LockOutlinedIcon sx={{ fontSize: 28, color: '#e74c3c' }} />
                  </Box>
                  <Typography sx={{ color: T.text, fontWeight: 800, mb: 0.5, fontSize: '1.3rem' }}>Mot de passe compromis</Typography>
                  <Typography variant="body2" sx={{ color: T.muted, lineHeight: 1.7, fontSize: '0.88rem' }}>
                    Ce mot de passe a été détecté dans une fuite de données. Changez-le avant de vous connecter.
                  </Typography>
                </Box>
                <Button component={Link} to="/forgot-password" fullWidth variant="contained"
                  sx={{
                    py: 1.6, mb: 2, background: '#c0392b', color: 'white', fontWeight: 700, fontSize: '0.95rem',
                    borderRadius: T.radius.btn, textTransform: 'none', boxShadow: '0 4px 16px rgba(192,57,43,0.2)',
                    '&:hover': { background: '#a93226' },
                  }}>
                  Changer mon mot de passe
                </Button>
                <Button onClick={() => { setStep('credentials'); setError(''); }} fullWidth
                  sx={{ color: 'rgba(0,0,0,0.4)', textTransform: 'none', '&:hover': { color: T.purple, background: 'transparent' } }}>
                  Retour
                </Button>
              </Box>
            )}

            {step === 'otp' && (
              <OtpStep method={method} twoFactorSession={twoFactorSession} hints={hints} onBack={() => { setError(''); setStep('credentials'); }} onError={setError} rememberMe={rememberMe} />
            )}

            {step === 'verify-email' && (
              <form onSubmit={handleVerifyEmail}>
                <Box sx={{ textAlign: 'center', mb: 3.5 }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: '16px', mx: 'auto', mb: 2, background: 'rgba(85,37,131,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EmailOutlinedIcon sx={{ fontSize: 26, color: T.purple }} />
                  </Box>
                  <Typography sx={{ color: T.text, fontWeight: 800, mb: 0.5, fontSize: '1.3rem' }}>Activez votre compte</Typography>
                  <Typography variant="body2" sx={{ color: T.muted, lineHeight: 1.6 }}>
                    Un code de vérification a été envoyé à votre adresse email.
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={labelSx}>Code de vérification</Typography>
                  <TextField fullWidth autoFocus
                    value={verifCode}
                    onChange={e => setVerifCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    sx={{
                      ...inputSx,
                      '& input': { color: T.text, py: 1.6, letterSpacing: '0.4em', fontSize: '1.5rem', fontWeight: 700, textAlign: 'center', '&::placeholder': { color: 'rgba(0,0,0,0.12)' } },
                    }}
                  />
                </Box>

                <Button type="submit" fullWidth variant="contained" disabled={verifLoading || verifCode.length !== 6} endIcon={!verifLoading && <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
                  sx={{
                    py: 1.6, mb: 3, background: T.purple, color: 'white', fontWeight: 700, fontSize: '0.95rem',
                    borderRadius: T.radius.btn, textTransform: 'none', boxShadow: '0 4px 16px rgba(85,37,131,0.25)',
                    '&:hover': { background: T.purpleDark },
                    '&:disabled': { background: 'rgba(85,37,131,0.12)', color: 'rgba(0,0,0,0.25)' },
                  }}>
                  {verifLoading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Activer mon compte'}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  {verifResent ? (
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600, fontSize: '0.85rem' }}><CheckIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.3 }} />Code renvoyé</Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.4)', fontSize: '0.85rem' }}>
                      Pas recu ?{' '}
                      <Box component="span" onClick={!verifResending ? handleVerifResend : undefined}
                        sx={{ color: T.purple, fontWeight: 700, cursor: verifResending ? 'default' : 'pointer', textDecoration: 'underline', opacity: verifResending ? 0.5 : 1 }}>
                        {verifResending ? 'Envoi...' : 'Renvoyer'}
                      </Box>
                    </Typography>
                  )}
                  <Button onClick={() => { setStep('credentials'); setError(''); setVerifCode(''); }}
                    sx={{ mt: 1, color: 'rgba(0,0,0,0.35)', textTransform: 'none', fontSize: '0.82rem', '&:hover': { color: T.purple, background: 'transparent' } }}>
                    Retour
                  </Button>
                </Box>
              </form>
            )}
          </Box>

          <Typography sx={{ textAlign: 'center', mt: 3, color: 'rgba(0,0,0,0.25)', fontSize: '0.7rem', letterSpacing: '0.03em' }}>
            Connexion sécurisée SSL 256 bits
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
